import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useDebouncedValue } from './useDebouncedValue';

/**
 * URL-backed list state, the companion to `useTableSort`. A list page keeps
 * its search text and page number in the query string (`?q=&page=`) so a
 * filtered view survives reload and can be sent to a colleague, and browser
 * back returns to the previous search rather than the previous page.
 *
 * The convention every list page follows:
 * - `q` mirrors the debounced search; typing never spams history.
 * - `page` resets whenever the search changes (and `useTableSort` resets it
 *   on sort), so a narrowed list never opens on an empty page 3.
 */

/** The query-string keys, named once. */
export const LIST_QUERY_KEYS = {
  search: 'q',
  page: 'page',
} as const;

/**
 * Search text bound to `?q=`.
 *
 * @param key - The query-string key; override only when two lists share a URL.
 * @returns The live input value, its setter, and the debounced value to query with.
 */
export function useUrlSearch(
  key: string = LIST_QUERY_KEYS.search,
  /** The page key this search resets; a prefixed table resets its own page. */
  pageKey: string = LIST_QUERY_KEYS.page,
): {
  readonly search: string;
  readonly setSearch: (value: string) => void;
  readonly debounced: string;
} {
  const [params, setParams] = useSearchParams();
  // Kept across the URL rewrite, so the page's Back still knows its origin.
  const { state } = useLocation();
  const urlSearch = params.get(key) ?? '';
  const [search, setSearch] = useState(urlSearch);
  const debounced = useDebouncedValue(search.trim());

  // What the effect last saw, to tell typing apart from the URL moving.
  const seen = useRef({ debounced, urlSearch });

  useEffect(() => {
    const typed = debounced !== seen.current.debounced;
    const urlMoved = urlSearch !== seen.current.urlSearch;
    seen.current = { debounced, urlSearch };

    // The URL moved to another search without anyone typing (browser Back, a
    // link to a filtered view): the box follows the URL. Writing the box's
    // text back would undo the navigation.
    if (urlMoved && !typed) {
      if (urlSearch !== debounced) setSearch(urlSearch);
      return;
    }
    // Only navigate when the typed text differs from the URL. This effect
    // re-runs on every URL change (setParams changes with it), and a rewrite
    // built from an older URL can land after a newer one (a sort click just
    // after a search, #645) and undo it.
    if (!typed || debounced === urlSearch) return;
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        const current = next.get(key) ?? '';
        if (debounced === current) return previous;
        if (debounced) next.set(key, debounced);
        else next.delete(key);
        next.delete(pageKey);
        return next;
      },
      { replace: true, state },
    );
    // `state` rides along with the rewrite; it is deliberately not a dependency.
  }, [debounced, urlSearch, key, pageKey, setParams]);

  return { search, setSearch, debounced };
}

/**
 * The 1-based page bound to `?page=`.
 *
 * @param key - The query-string key; override only when two lists share a URL.
 * @returns The page and a setter that pushes history (back returns to the previous page).
 */
export function useUrlPage(key: string = LIST_QUERY_KEYS.page): {
  readonly page: number;
  readonly setPage: (page: number) => void;
} {
  const [params, setParams] = useSearchParams();
  // Kept across the URL rewrite, so the page's Back still knows its origin.
  const { state } = useLocation();
  const page = Math.max(1, Number(params.get(key) ?? '1') || 1);

  function setPage(next: number): void {
    setParams((previous) => {
      const nextParams = new URLSearchParams(previous);
      if (next <= 1) nextParams.delete(key);
      else nextParams.set(key, String(next));
      return nextParams;
    }, { state });
  }

  return { page, setPage };
}
